import { useCookieStore } from '~/stores/cookieStore'; // Importera cookieStore

import type {
  CartModel,
  CollectionModel,
  CreateCartLineRequest,
  Edges,
  PageModel,
  ProductModel,
  MenuItemModel,
  MenuModel,
  UpdateCartLineRequest,
} from "~/types/shopify";

import gql from "graphql-tag";
import { useMutation } from '@vue/apollo-composable'



const menuItemFragment = gql`
  fragment menuItem on MenuItem {
    id
    title
    url
    children {
      id
      title
      url
      # Lägg gärna till fler nivåer om det behövs
    }
  }
`;

const menuFragment = gql`
  fragment menu on Menu {
    id
    title
    items {
      title
      url
      type
      resource {
        __typename
        ... on Page {
          id
          title
        }
        ... on Collection {
          id
          title
        }
        ... on Product {
          id
          title
        }
        ... on Article {
          id
          title
        }
      }
      items {
        title
        url
        type
        resource {
          __typename
          ... on Page {
            id
            title
          }
          ... on Collection {
            id
            title
          }
          ... on Product {
            id
            title
          }
          ... on Article {
            id
            title
          }
        }
        items {
          title
          url
          type
          resource {
            __typename
            ... on Page {
              id
              title
            }
            ... on Collection {
              id
              title
            }
            ... on Product {
              id
              title
            }
            ... on Article {
              id
              title
            }
          }
        }
      }
    }
  }
`;


export const imageFragment = gql`
    fragment image on Image {
        url
        altText
        width
        height
    }
`;

export const priceFragment = gql`
    fragment price on MoneyV2 {
        amount
        currencyCode
    }
`;

export const seoFragment = gql`
    fragment seo on SEO {
        title
        description
    }
`;

export const pageFragment = gql`
  fragment page on Page {
    title
    handle
    id
    body
    bodySummary
    seo {
      ...seo
    }
    coverImage: metafield(namespace: "custom", key: "coverimage") {
      value
    }
    customBackendpage: metafield(namespace: "custom", key: "backendpage") {
      value
    }
    featuredPages: metafield(namespace: "custom", key: "featuredpages") {
      value
    }
    featuredCollections: metafield(namespace: "custom", key: "featuredcollections") {
      value
    }
      parentPage: metafield(namespace: "custom", key: "parentpage") {
      value
    }
    menudescription: metafield(namespace: "custom", key: "menudescription") {
      value
    }
}
  ${seoFragment}
`;




export const collectionFragment = gql`
    fragment collection on Collection {
        id
        handle
        title
        description
        descriptionHtml
        image {
            ...image
        }
        seo {
            ...seo
        }
        coverImage: metafield(namespace: "custom", key: "coverimage") {
            value
        }
        featuredPages: metafield(namespace: "custom", key: "featuredpages") {
            value
        }
    }
    ${seoFragment}
    ${imageFragment}
`;

export const productFragment = gql`
    fragment product on Product {
        id
        title
        handle
        vendor
        productType
        description
        descriptionHtml
        options {
            id
            name
            values
        }
        priceRange {
            minVariantPrice {
                ...price
            }
            maxVariantPrice {
                ...price
            }
        }
        seo {
            ...seo
        }
        featuredImage {
            ...image
        }
       images(first: 250) {
        nodes {
            ...image
        }
        }
        variants(first: 250) {
            edges {
                node {
                    id
                    title
                    availableForSale
                    selectedOptions {
                        name
                        value
                    }
                    price {
                        ...price
                    }
                }
            }
        }
        tags
        customHubType: metafield(namespace: "custom", key: "hub_type") {
            value
        }
        shortDescription: metafield(namespace: "custom", key: "short_description") {
            value
        }
        specifications: metafield(namespace: "custom", key: "specifications") {
            value
        }
        wheelsetRiderWeightOptions: metafield(namespace: "custom", key: "wheelset_rider_weight_options") {
            value
        }
        wheelsetRimTypeOptions: metafield(namespace: "custom", key: "wheelset_rim_type_options") {
            value
        }
        wheelsetRimHeightOptions: metafield(namespace: "custom", key: "wheelset_rim_height_options") {
            value
        }
        wheelsetTireWidthOptions: metafield(namespace: "custom", key: "wheelset_tire_width_options") {
            value
        }
        wheelsetRimBrandOptions: metafield(namespace: "custom", key: "wheelset_rim_brand_options") {
            value
        }
        
        
            
    }
    ${seoFragment}
    ${priceFragment}
    ${imageFragment}
`;

const cartFragment = gql`
    fragment cart on Cart {
        id
        checkoutUrl
        totalQuantity
        cost {
            subtotalAmount { ...price }
            totalAmount { ...price }
           
        }
        discountCodes {
            code
            applicable
        }
        lines(first: 100) {
            edges {
                node {
                    id
                    quantity
                    cost {
                        totalAmount {
                            ...price
                        }
                    }
                    merchandise {
                        ... on ProductVariant {
                            id
                            title
                            sku
                            selectedOptions {
                                name
                                value
                            }
                            product {
                                ...product
                            }
                        }
                    }
                }
            }
        }
    }
    ${productFragment}
    ${priceFragment}
`;

export function useCartCookie() {
  const cookieStore = useCookieStore(); // Accessa cookieStore

  if (!cookieStore.consentGiven) {
    console.warn("Cookies are disabled because the user has not given consent.");
    return null; // Returnera null eller hantera detta fall på annat sätt
  }

  return useCookie("cart", {
      sameSite: false,
  });
}

export function useCollections() {
  type Response = {
    collections: Edges<CollectionModel>;
  };

  const query = gql`
        query getCollections {
            collections(first: 100, sortKey: TITLE) {
                edges {
                    node {
                        ...collection
                    }
                }
            }
        }
        ${collectionFragment}
    `;

  return useAsyncQuery<Response | undefined>(query);
}

export function useCollectionById(id: string) {
  type Response = {
    node: CollectionModel;
  };

  const query = gql`
      query getCollectionById($id: ID!) {
        node(id: $id) {
          ... on Collection {
            ...collection
          }
        }
      }
      ${collectionFragment}
    `;

  return useAsyncQuery<Response | undefined>(query, { id });
}

export function useCollection(collection: string, first?: number) {
  type Response = {
    collection: CollectionModel;
  };

  const query = gql`
        query getCollection($collection: String!, $first: Int!) {
            collection(handle: $collection) {
                ...collection
                products(first: $first) {
                    edges {
                        node {
                            ...product
                        }
                    }
                }
            }
        }
        ${productFragment}
        ${collectionFragment}
    `;

  return useAsyncQuery<Response | undefined>(query, {
    collection,
    first: first ?? 100,
  });
}

export function useProduct(product: string) {
  type Response = {
    product: ProductModel;
  };

  const query = gql`
        query getProduct($product: String!) {
            product(handle: $product) {
                ...product
            }
        }
        ${productFragment}
    `;

  return useAsyncQuery<Response | undefined>(query, {
    product,
  });
}

export function useRecommendations(product: string) {
  type Response = {
    productRecommendations: Array<ProductModel>;
  };

  const query = gql`
        query getRecommendations($product: ID!) {
            productRecommendations(productId: $product) {
                ...product
            }
        }
        ${productFragment}
    `;

  return useAsyncQuery<Response | undefined>(query, {
    product,
  });
}

export function usePages() {
  type Response = {
    pages: Edges<PageModel>;
  };

  const query = gql`
        query getPages {
            pages(first: 100) {
                edges {
                    node {
                        ...page
                    }
                }
            }
        }
        ${pageFragment}
    `;

  return useAsyncQuery<Response | undefined>(query);
}

export function usePageById(id: string) {
  type Response = {
    node: PageModel;
  };

  const query = gql`
      query getPageById($id: ID!) {
        node(id: $id) {
          ... on Page {
            ...page
          }
        }
      }
      ${pageFragment}
    `;

  return useAsyncQuery<Response | undefined>(query, {
    id,
  });
}

export function usePage(page: string) {
  type Response = {
    pageByHandle: PageModel;
  };

  const query = gql`
        query getPage($page: String!) {
            pageByHandle(handle: $page) {
                ...page
            }
        }
        ${pageFragment}
    `;

  return useAsyncQuery<Response | undefined>(query, {
    page,
  });
}

export function useCart(cartId: string) {
  type Response = {
    cart: CartModel;
  };

  const query = gql`
        query getCart($cart: ID!) {
            cart(id: $cart) {
                ...cart
            }
        }
        ${cartFragment}
    `;

  const { resolveClient } = useApolloClient();
  const apolloClient = resolveClient();


  return async function fetchCart() {
    try {
      const response = await apolloClient.query<Response>({
        query,
        variables: { cart: cartId },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  };
}

export function useCreateCart() {
  type Response = {
    cartCreate: {
      cart: CartModel;
    };
  };

  const mutation = gql`
        mutation createCart($lines: [CartLineInput!]) {
            cartCreate(input: { lines: $lines }) {
                cart {
                    ...cart
                }
            }
        }
        ${cartFragment}
    `;

  return useMutation<Response>(mutation);
}

export function useUpdateCartLines() {
  type Response = {};

  type Variables = {
    cart: string;
    lines: Array<UpdateCartLineRequest>;
  };

  const mutation = gql`
        mutation editCartItems($cart: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cart, lines: $lines) {
                cart {
                    ...cart
                }
            }
        }
        ${cartFragment}
    `;

  return useMutation<Response, Variables>(mutation);
}

export function useCreateCartLine() {
  type Response = {};

  type Variables = {
    cart: string;
    lines: Array<CreateCartLineRequest>;
  };

  const mutation = gql`
        mutation addToCart($cart: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cart, lines: $lines) {
                cart {
                    ...cart
                }
            }
        }
        ${cartFragment}
    `;

  return useMutation<Response, Variables>(mutation);
}

export function useDeleteCartLine() {
  type Response = {};

  type Variables = {
    cart: string;
    lines: Array<string>;
  };

  const mutation = gql`
        mutation removeFromCart($cart: ID!, $lines: [ID!]!) {
            cartLinesRemove(cartId: $cart, lineIds: $lines) {
                cart {
                    ...cart
                }
            }
        }
        ${cartFragment}
    `;

  return useMutation<Response, Variables>(mutation);
}

export function useMenu(handle: string) {
  type Response = {
    menu: MenuModel;
  };

  const query = gql`
        query getMenu($handle: String!) {
            menu(handle: $handle) {
                ...menu
            }
        }
        ${menuFragment}
    `;

  return useAsyncQuery<Response | undefined>(query, {
    handle,
  });
}

const loginMutation = gql`
  mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;

export function useLogin() {
  type Response = {
    customerAccessTokenCreate: {
      customerAccessToken: {
        accessToken: string;
        expiresAt: string;
      };
      customerUserErrors: Array<{
        code: string;
        field: string;
        message: string;
      }>;
    };
  };

  return useMutation<Response>(loginMutation);
}

export const CUSTOMER_QUERY = gql`
  query customer($customerAccessToken: String!) {
    customer(customerAccessToken: $customerAccessToken) {
      id
      email
      firstName
      lastName
      phone
      addresses {
        id
        address1
        city
        country
      }
    }
  }
`;

export function useCustomer() {
  const { result, loading, error } = useQuery(CUSTOMER_QUERY, {
    customerAccessToken: localStorage.getItem('shopifyAccessToken'),
  }, {
    clientId: 'customer',
  });

  return {
    customer: result,
    loading,
    error,
  };
}


const customerAccessTokenCreateMutation = gql`
  mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;

export function useCustomerAccessTokenCreate() {
  type Response = {
    customerAccessTokenCreate: {
      customerAccessToken: {
        accessToken: string;
        expiresAt: string;
      };
      customerUserErrors: Array<{
        code: string;
        field: string;
        message: string;
      }>;
    };
  };

  return useMutation<Response>(customerAccessTokenCreateMutation);
}

const CART_DISCOUNT_CODES_UPDATE = gql`
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...cart
      }
      userErrors {
        field
        message
      }
    }
  }
  ${cartFragment}
`

export function useCartDiscountCodesUpdate() {
  return useMutation(CART_DISCOUNT_CODES_UPDATE)
}